import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { transactionService } from '../../../services/supabaseService';

const ViewAllModal = ({ transactions, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
    <div className="bg-surface rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-text-primary">All Transactions</h3>
        <button onClick={onClose} className="p-2 hover:bg-surface-hover rounded-lg transition-colors duration-200">
          <Icon name="X" size={18} />
        </button>
      </div>
      <div className="overflow-y-auto flex-1 p-4 space-y-2">
        {transactions?.length === 0 && (
          <p className="text-center text-text-muted py-8">No transactions found</p>
        )}
        {transactions?.map((t) => (
          <div key={t?.id} className="flex items-center justify-between p-3 border border-border rounded-lg hover:bg-surface-hover transition-colors duration-200">
            <div>
              <p className="text-sm font-medium text-text-primary">{t?.reference || t?.id?.slice(0, 8)}</p>
              <p className="text-xs text-text-muted">{t?.userName || 'Unknown'} • {t?.paymentMethod || 'mpesa'}</p>
              <p className="text-xs text-text-muted">{t?.createdAt ? new Date(t?.createdAt)?.toLocaleString() : ''}</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-text-primary">KES {parseFloat(t?.amount || 0)?.toLocaleString()}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                t?.status === 'completed' ? 'bg-success-100 text-success' :
                t?.status === 'pending' ? 'bg-warning-100 text-warning' : 'bg-error-100 text-error'
              }`}>{t?.status}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const FinancialDashboard = ({ onRefresh }) => {
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [financialStats, setFinancialStats] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [settlementQueue, setSettlementQueue] = useState([]);
  const [showViewAll, setShowViewAll] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const fetchFinancialData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Get date range based on period
      const now = new Date();
      let startDate;
      if (selectedPeriod === 'today') {
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())?.toISOString();
      } else if (selectedPeriod === 'week') {
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)?.toISOString();
      } else {
        startDate = new Date(now.getFullYear(), now.getMonth(), 1)?.toISOString();
      }

      // Fetch wallet_transactions for revenue stats
      const { data: walletTxns, error: wtErr } = await supabase?.from('wallet_transactions')?.select('amount, category, ledger_type, status, created_at')?.gte('created_at', startDate)?.eq('status', 'completed');
      if (wtErr) throw wtErr;

      // Compute stats from wallet_transactions
      const credits = walletTxns?.filter(t => t?.ledger_type === 'credit') || [];
      const commissions = walletTxns?.filter(t => t?.category === 'commission') || [];
      const withdrawals = walletTxns?.filter(t => t?.category === 'withdrawal') || [];
      const ridePayments = walletTxns?.filter(t => t?.category === 'ride_payment') || [];
      const deliveryPayments = walletTxns?.filter(t => t?.category === 'delivery_payment') || [];

      const totalRevenue = credits?.reduce((s, t) => s + parseFloat(t?.amount || 0), 0);
      const totalCommissions = commissions?.reduce((s, t) => s + parseFloat(t?.amount || 0), 0);
      const totalWithdrawals = withdrawals?.reduce((s, t) => s + parseFloat(t?.amount || 0), 0);
      const mpesaVolume = ridePayments?.reduce((s, t) => s + parseFloat(t?.amount || 0), 0) +
                          deliveryPayments?.reduce((s, t) => s + parseFloat(t?.amount || 0), 0);

      // Fetch pending payout requests for settlement queue
      const { data: payouts, error: payErr } = await supabase?.from('payout_requests')?.select(`
          id, amount, status, created_at, bank_name, account_name,
          driver:user_profiles!payout_requests_driver_id_fkey(full_name)
        `)?.in('status', ['pending', 'approved'])?.order('created_at', { ascending: false })?.limit(10);
      if (payErr) throw payErr;

      // Fetch recent transactions from transactions table
      const recent = await transactionService?.getAllTransactions({ limit: 5 });
      const all = await transactionService?.getAllTransactions({ limit: 100 });

      // Pending settlements total
      const pendingTotal = payouts?.filter(p => p?.status === 'pending')?.reduce((s, p) => s + parseFloat(p?.amount || 0), 0);

      setFinancialStats({
        totalRevenue,
        commissions: totalCommissions,
        pendingSettlements: pendingTotal,
        transactionCount: walletTxns?.length || 0,
        mpesaVolume,
        cardPayments: 0,
        cashPayments: 0,
        settlements: totalRevenue - totalWithdrawals,
      });
      setRecentTransactions(recent);
      setAllTransactions(all);
      setSettlementQueue(payouts?.map(p => ({
        id: p?.id,
        driver: p?.driver?.full_name || 'Unknown Driver',
        amount: parseFloat(p?.amount || 0),
        netAmount: parseFloat(p?.amount || 0),
        status: p?.status,
        bankName: p?.bank_name,
        accountName: p?.account_name,
      })) || []);
    } catch (err) {
      setError(err?.message || 'Failed to load financial data');
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    fetchFinancialData();
  }, [fetchFinancialData]);

  // Expose refresh to parent
  useEffect(() => {
    if (onRefresh) onRefresh(fetchFinancialData);
  }, [onRefresh, fetchFinancialData]);

  const handleProcessSettlement = async (requestId) => {
    setProcessingId(requestId);
    try {
      const { data, error: rpcErr } = await supabase?.rpc('approve_payout', {
        p_request_id: requestId,
      });
      if (rpcErr) throw rpcErr;
      setSuccessMsg(`Settlement processed successfully`);
      setTimeout(() => setSuccessMsg(''), 3000);
      fetchFinancialData();
    } catch (err) {
      setError(err?.message || 'Failed to process settlement');
      setTimeout(() => setError(null), 4000);
    } finally {
      setProcessingId(null);
    }
  };

  const formatCurrency = (amount) => `KES ${parseFloat(amount || 0)?.toLocaleString()}`;

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const diff = now - new Date(timestamp);
    const minutes = Math.floor(diff / 60000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(diff / 3600000);
    return `${hours}h ago`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'approved': return 'secondary';
      case 'failed': return 'error';
      default: return 'text-muted';
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'mpesa': case 'M-Pesa': return 'Smartphone';
      case 'card': case 'Card': return 'CreditCard';
      case 'cash': case 'Cash': return 'Banknote';
      default: return 'DollarSign';
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="card animate-pulse">
          <div className="h-6 bg-surface-hover rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-2 gap-3">
            {[1,2,3,4]?.map(i => <div key={i} className="h-20 bg-surface-hover rounded-lg"></div>)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showViewAll && (
        <ViewAllModal transactions={allTransactions} onClose={() => setShowViewAll(false)} />
      )}
      {successMsg && (
        <div className="p-3 bg-success-50 border border-success-200 rounded-lg text-success text-sm">{successMsg}</div>
      )}
      {error && (
        <div className="p-3 bg-error-50 border border-error-200 rounded-lg text-error text-sm">{error}</div>
      )}
      {/* Financial Overview Card */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-text-primary">Financial Dashboard</h3>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e?.target?.value)}
            className="input-field text-sm py-1 px-2 min-w-0"
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        </div>

        {/* Key Financial Metrics */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="p-3 bg-success-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <Icon name="TrendingUp" size={16} color="var(--color-success)" />
              <span className="text-sm font-medium text-success">Total Revenue</span>
            </div>
            <p className="text-xl font-bold text-text-primary font-mono">
              {formatCurrency(financialStats?.totalRevenue)}
            </p>
          </div>

          <div className="p-3 bg-secondary-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <Icon name="Percent" size={16} color="var(--color-secondary)" />
              <span className="text-sm font-medium text-secondary">Commissions</span>
            </div>
            <p className="text-xl font-bold text-text-primary font-mono">
              {formatCurrency(financialStats?.commissions)}
            </p>
          </div>

          <div className="p-3 bg-warning-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <Icon name="Clock" size={16} color="var(--color-warning)" />
              <span className="text-sm font-medium text-warning">Pending</span>
            </div>
            <p className="text-xl font-bold text-text-primary font-mono">
              {formatCurrency(financialStats?.pendingSettlements)}
            </p>
          </div>

          <div className="p-3 bg-primary-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-1">
              <Icon name="Activity" size={16} color="var(--color-primary)" />
              <span className="text-sm font-medium text-primary">Transactions</span>
            </div>
            <p className="text-xl font-bold text-text-primary font-mono">
              {(financialStats?.transactionCount || 0)?.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Payment Methods Breakdown */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-text-primary">Payment Volume</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-2 bg-surface-hover rounded">
              <div className="flex items-center space-x-2">
                <Icon name="Smartphone" size={16} color="var(--color-success)" />
                <span className="text-sm text-text-primary">Ride & Delivery Payments</span>
              </div>
              <span className="text-sm font-medium text-text-primary">
                {formatCurrency(financialStats?.mpesaVolume)}
              </span>
            </div>
            <div className="flex items-center justify-between p-2 bg-surface-hover rounded">
              <div className="flex items-center space-x-2">
                <Icon name="Percent" size={16} color="var(--color-secondary)" />
                <span className="text-sm text-text-primary">Commission Earned</span>
              </div>
              <span className="text-sm font-medium text-text-primary">
                {formatCurrency(financialStats?.commissions)}
              </span>
            </div>
            <div className="flex items-center justify-between p-2 bg-surface-hover rounded">
              <div className="flex items-center space-x-2">
                <Icon name="ArrowUpRight" size={16} color="var(--color-warning)" />
                <span className="text-sm text-text-primary">Net Settled</span>
              </div>
              <span className="text-sm font-medium text-text-primary">
                {formatCurrency(financialStats?.settlements)}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Recent Transactions */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-text-primary">Recent Transactions</h4>
          <button
            onClick={() => setShowViewAll(true)}
            className="text-sm text-secondary hover:text-secondary-700 transition-colors duration-200"
          >
            View All
          </button>
        </div>

        {recentTransactions?.length === 0 ? (
          <p className="text-center text-text-muted py-4 text-sm">No transactions yet</p>
        ) : (
          <div className="space-y-2">
            {recentTransactions?.map((transaction) => (
              <div key={transaction?.id} className="flex items-center justify-between p-2 border border-border rounded hover:bg-surface-hover transition-colors duration-200">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <Icon name={getPaymentMethodIcon(transaction?.paymentMethod)} size={12} color="var(--color-primary)" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">{transaction?.reference || transaction?.id?.slice(0, 8)}</p>
                    <p className="text-xs text-text-muted">
                      {transaction?.userName || 'Unknown'} • {transaction?.paymentMethod || 'mpesa'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-text-primary">
                    {formatCurrency(transaction?.amount)}
                  </p>
                  <div className="flex items-center space-x-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full bg-${getStatusColor(transaction?.status)}-100 text-${getStatusColor(transaction?.status)}`}>
                      {transaction?.status}
                    </span>
                    <span className="text-xs text-text-muted">{formatTime(transaction?.createdAt)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Settlement Queue */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-medium text-text-primary">Settlement Queue</h4>
          <span className="text-xs text-text-muted">{settlementQueue?.filter(s => s?.status === 'pending')?.length} pending</span>
        </div>

        {settlementQueue?.length === 0 ? (
          <p className="text-center text-text-muted py-4 text-sm">No pending settlements</p>
        ) : (
          <div className="space-y-2">
            {settlementQueue?.map((settlement) => (
              <div key={settlement?.id} className="p-3 border border-border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-text-primary">{settlement?.driver}</span>
                    <span className={`text-xs px-2 py-1 rounded-full bg-${getStatusColor(settlement?.status)}-100 text-${getStatusColor(settlement?.status)}`}>
                      {settlement?.status}
                    </span>
                  </div>
                  <span className="text-sm font-bold text-text-primary">
                    {formatCurrency(settlement?.netAmount)}
                  </span>
                </div>
                {settlement?.bankName && (
                  <p className="text-xs text-text-muted mb-2">{settlement?.bankName} • {settlement?.accountName}</p>
                )}
                {settlement?.status === 'pending' && (
                  <button
                    onClick={() => handleProcessSettlement(settlement?.id)}
                    disabled={processingId === settlement?.id}
                    className="btn-primary text-xs px-3 py-1 disabled:opacity-50"
                  >
                    {processingId === settlement?.id ? 'Processing...' : 'Process'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialDashboard;